import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArchiveBoxIcon,
  ArrowRightIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import CustomDialog from "../../components/modals/CustomDialog/CustomDialog";
import { Button } from "../../components/forms/Buttons/Button";
import { CHIP_TONES } from "../../components/QuestionList/FilterChips";
import { changeExamStatus } from "../../services/Exams/ExamsService";
import { getApiErrorCode, getCodedErrorMessage } from "../../utils/apiError";
import { focusRing } from "./TestBuilder/ui";

// An exam's status (ТЗ 4.1): a draft is new and unseen, an active exam is what students see and start, an
// archived one is read-only. Shared by the exam list and the exam's page.

// The statuses with their tones, in the order of the list's chips.
export const EXAM_STATUS_TONES = { active: CHIP_TONES.green, draft: CHIP_TONES.amber, archived: CHIP_TONES.slate };

// "active", "draft", "archived", another status as sent, or "" when the exam has none.
export const examStatus = (exam) => (typeof exam?.status === "string" ? exam.status.trim().toLowerCase() : "");

export const isArchived = (exam) => examStatus(exam) === "archived";

export const examStatusLabel = (t, status) =>
  EXAM_STATUS_TONES[status] ? t(`exam_bank.status.${status}`) : status || "";

// The moves offered from each status, the usual next step first. The API takes any; into active only with a
// test that has questions (400 exam_empty).
export const STATUS_MOVES = {
  draft: ["active", "archived"],
  active: ["archived", "draft"],
  archived: ["active", "draft"],
};

// Deleting is offered for a draft only (its page's "⋯"): students never saw it. The server keeps an exam that was
// taken (409 exam_has_attempts: it goes to the archive instead) and an archived one (409 exam_archived, first).
export const isDeletable = (exam) => examStatus(exam) === "draft";

// Taking an exam away from students, or into the read-only archive, is confirmed first.
export const needsConfirm = (from, to) => from === "active" || to === "archived";

// The key of a move's hint and confirmation: archiving a draft takes nothing from students, who never saw it.
const moveTextKey = (from, to) => (from === "draft" && to === "archived" ? "archived_from_draft" : to);

export const MOVE_ICONS = { active: CheckCircleIcon, archived: ArchiveBoxIcon, draft: ArrowUturnLeftIcon };

export function ExamStatusBadge({ status, className = "" }) {
  const [t] = useTranslation();
  if (!status) return null;
  const tone = EXAM_STATUS_TONES[status] || CHIP_TONES.slate;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${tone.badge} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden="true" />
      {examStatusLabel(t, status)}
    </span>
  );
}

// A move's name and what it means for students (`from`: the exam's status now).
function MoveHint({ status, from, className = "" }) {
  const [t] = useTranslation();
  const Icon = MOVE_ICONS[status] || CheckCircleIcon;
  return (
    <span className={`block ${className}`}>
      <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
        <Icon className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
        {t(`exam_bank.status_actions.${status}`)}
      </span>
      <span className="mt-0.5 block text-sm text-slate-500">{t(`exam_bank.status_actions.hint.${moveTextKey(from, status)}`)}</span>
    </span>
  );
}

// Sends a status change: {exam} (the exam with its new status) or {error, code} (the message to show and the
// refusal's code), and a toast on success. `pending` is the status being sent.
export function useChangeExamStatus() {
  const [t] = useTranslation();
  const [pending, setPending] = useState(null);
  const change = useCallback(
    async (exam, status) => {
      setPending(status);
      try {
        const updated = await changeExamStatus({ uuid: exam.uuid, status });
        toast.success(t(`exam_bank.status_actions.toast.${status}`, { defaultValue: t("exam_bank.status_actions.change") }));
        return { exam: { ...exam, status: updated?.status || status, updated: updated?.updated ?? exam.updated } };
      } catch (err) {
        return { error: getCodedErrorMessage(t, err, t("exam_bank.status_actions.failed")), code: getApiErrorCode(err) };
      } finally {
        setPending(null);
      }
    },
    [t]
  );
  return { change, pending };
}

/**
 * Changes an exam's status. With `target` it confirms that one move (archiving, or taking an active exam away
 * from students); without it the user picks among the moves of the exam's status, each explained. A refusal
 * (exam_empty, exam_archived, ...) stays in the dialog and takes the focus. An exam known to have no test (the
 * list's tests_count 0) is not activated from here: the dialog says so and leads to the exam's page, where tests
 * are added. `onChanged(exam)` gets the exam with its new status.
 */
export function ExamStatusDialog({ exam, target = null, isOpen, onClose, onChanged }) {
  const [t] = useTranslation();
  const { change, pending } = useChangeExamStatus();
  const current = examStatus(exam);
  const moves = STATUS_MOVES[current] || ["active", "draft", "archived"].filter((s) => s !== current);
  const [chosen, setChosen] = useState(target || moves[0]);
  const [error, setError] = useState("");
  const errorRef = useRef(null);
  const groupId = useId();

  useEffect(() => {
    if (!isOpen) return;
    setChosen(target || (STATUS_MOVES[examStatus(exam)] || [])[0] || "active");
    setError("");
  }, [isOpen, target, exam]);

  // The pressed button lost the focus while it was sending: the refusal takes it, inside the dialog.
  useEffect(() => {
    if (error && !pending) errorRef.current?.focus();
  }, [error, pending]);

  const close = () => {
    if (!pending) onClose();
  };

  const submit = async () => {
    if (!chosen || pending) return;
    setError("");
    const result = await change(exam, chosen);
    if (result.error) {
      setError(result.error);
      return;
    }
    onChanged?.(result.exam);
    onClose();
  };

  const name = exam?.name?.trim() || t("exam_bank.untitled");
  const confirming = !!target;
  const title = confirming
    ? t(`exam_bank.status_actions.confirm.${target}_title`, { defaultValue: t(`exam_bank.status_actions.${target}`) })
    : t("exam_bank.status_actions.dialog_title");
  const ActionIcon = MOVE_ICONS[chosen];
  // Activation needs a test with questions (400 exam_empty); without any test it is refused for sure. (A test
  // without questions is known only to the exam's page and the server.)
  const needsTest = chosen === "active" && exam?.tests_count === 0;

  return (
    <CustomDialog isOpen={isOpen} closeModal={close} headerText={title} maxWidth="max-w-lg">
      {confirming ? (
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          {t(`exam_bank.status_actions.confirm.${moveTextKey(current, target)}_text`, {
            name,
            defaultValue: t(`exam_bank.status_actions.hint.${moveTextKey(current, target)}`),
          })}
        </p>
      ) : (
        <>
          <p className="mt-1 break-words text-sm font-medium text-slate-900">{name}</p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            {t("exam_bank.status_actions.current")}
            <ExamStatusBadge status={current} />
          </p>
          {moves.length === 1 ? (
            // A status with one way on: said, nothing to choose.
            <MoveHint status={moves[0]} from={current} className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3" />
          ) : (
          <fieldset className="mt-4">
            <legend id={groupId} className="mb-2 text-sm font-medium text-slate-700">
              {t("exam_bank.status_actions.choose")}
            </legend>
            <div className="space-y-2">
              {moves.map((status) => {
                const selected = chosen === status;
                return (
                  <label
                    key={status}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition duration-200 ${
                      selected ? "border-blue-600 bg-blue-50/40 ring-4 ring-blue-600/10" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`exam-status-${groupId}`}
                      value={status}
                      checked={selected}
                      onChange={() => {
                        setChosen(status);
                        setError("");
                      }}
                      disabled={!!pending}
                      className="mt-1 h-4 w-4 shrink-0 border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
                    />
                    <MoveHint status={status} from={current} className="min-w-0" />
                  </label>
                );
              })}
            </div>
          </fieldset>
          )}
        </>
      )}

      {needsTest && (
        <p className="mt-4 flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900 ring-1 ring-inset ring-blue-600/10">
          <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
          <span className="min-w-0">
            {t(current === "archived" ? "exam_bank.status_actions.no_tests_archived" : "exam_bank.status_actions.no_tests")}
          </span>
        </p>
      )}

      {error && (
        <p
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 outline-none"
        >
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0">{error}</span>
        </p>
      )}

      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={close} disabled={!!pending} className="max-sm:h-11" />
        {needsTest ? (
          <Link
            to={`/exams_list/${exam.uuid}`}
            className={`inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-5 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 max-sm:h-11 ${focusRing}`}
          >
            {t("exam_bank.status_actions.open_exam")}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </Link>
        ) : (
          <Button
            type="button"
            color={chosen === "archived" ? "secondary-solid" : "primary"}
            Icon={ActionIcon}
            text={t(`exam_bank.status_actions.${chosen}`)}
            onClick={submit}
            loading={!!pending}
            className="max-sm:h-11"
          />
        )}
      </div>
    </CustomDialog>
  );
}
